import React, { useState, useEffect } from 'react';
import { usersApi } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { MagnifyingGlass, CaretLeft, CaretRight, UploadSimple, Plus, GenderMale, GenderFemale, CalendarBlank, PencilSimple, QrCode } from '@phosphor-icons/react';
import { toast } from 'sonner';
import BulkUploadDialog from '../../components/BulkUploadDialog';
import { SingleDuplicateDialog, BulkDuplicateDialog } from '../../components/DuplicateConfirmDialog';
import { validateRut } from '../../utils/rutValidator';
import { WorkerQrDialog } from '../../components/WorkerQrDialog';

const ITEMS_PER_PAGE = 10;
const EMPTY_FORM = { rut: '', first_name: '', last_name: '', cargo: '', sexo: '', email: '', phone: '', fecha_nacimiento: '' };
const FIELD_LABELS = { rut: 'RUT', first_name: 'Nombre', last_name: 'Apellido', cargo: 'Cargo', sexo: 'Sexo', email: 'Email', phone: 'Teléfono', fecha_nacimiento: 'Fecha de Nacimiento' };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContractorWorkersPage() {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [bulkDuplicates, setBulkDuplicates] = useState(null);
  const [pendingBulkData, setPendingBulkData] = useState(null);
  const [showAddWorker, setShowAddWorker] = useState(false);
  const [addingWorker, setAddingWorker] = useState(false);
  const [duplicateWorker, setDuplicateWorker] = useState(null);
  const [pendingWorkerData, setPendingWorkerData] = useState(null);
  const [workerForm, setWorkerForm] = useState(EMPTY_FORM);
  const [editingWorker, setEditingWorker] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [qrUser, setQrUser] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await usersApi.getAll({ role: 'worker', page_size: 999 });
      setWorkers(res.data.items || res.data);
    } catch { toast.error('Error al cargar trabajadores'); }
    finally { setLoading(false); }
  };

  const validateForm = () => {
    const errs = {};
    Object.keys(FIELD_LABELS).forEach(k => { if (!String(workerForm[k] || '').trim()) errs[k] = 'Obligatorio'; });
    if (!errs.rut) { const rc = validateRut(workerForm.rut); if (!rc.valid) errs.rut = rc.error; }
    if (!errs.email && !EMAIL_RE.test(workerForm.email.trim())) errs.email = 'Email inválido';
    setFieldErrors(errs);
    const missing = Object.keys(errs).filter(k => errs[k] === 'Obligatorio').map(k => FIELD_LABELS[k]);
    if (missing.length) toast.error(`Completa los campos obligatorios: ${missing.join(', ')}`);
    else if (Object.keys(errs).length) toast.error(Object.values(errs)[0]);
    return Object.keys(errs).length === 0;
  };

  const openCreate = () => { setEditingWorker(null); setWorkerForm(EMPTY_FORM); setFieldErrors({}); setShowAddWorker(true); };

  const openEdit = (w) => {
    setEditingWorker(w);
    setFieldErrors({});
    setWorkerForm({
      rut: w.rut || '', first_name: w.first_name || '', last_name: w.last_name || '', cargo: w.cargo || '',
      sexo: (w.sexo || '').toUpperCase().startsWith('F') ? 'FEMENINO' : (w.sexo ? 'MASCULINO' : ''),
      email: w.email || '', phone: w.phone || '', fecha_nacimiento: (w.fecha_nacimiento || '').slice(0, 10),
    });
    setShowAddWorker(true);
  };

  const handleEditWorker = async () => {
    setAddingWorker(true);
    try {
      const data = { ...workerForm, rut: validateRut(workerForm.rut).formatted };
      const res = await usersApi.contractorUpdateWorker(editingWorker.id, data);
      setWorkers(ws => ws.map(w => (w.id === editingWorker.id ? { ...w, ...res.data } : w)));
      toast.success('Trabajador actualizado');
      setShowAddWorker(false); setEditingWorker(null);
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al actualizar trabajador'); }
    finally { setAddingWorker(false); }
  };

  const handleAddWorker = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (editingWorker) { await handleEditWorker(); return; }
    workerForm.rut = validateRut(workerForm.rut).formatted;
    setAddingWorker(true);
    try {
      const autoPassword = workerForm.rut ? workerForm.rut.replace(/[^0-9kK]/g, '') : `${workerForm.email.split('@')[0]}123`;
      const submitData = {
        rut: workerForm.rut || null, first_name: workerForm.first_name, last_name: workerForm.last_name,
        cargo: workerForm.cargo || null, sexo: workerForm.sexo || null, email: workerForm.email,
        phone: workerForm.phone || null, fecha_nacimiento: workerForm.fecha_nacimiento || null,
        password: autoPassword, role: 'worker'
      };
      const checkRes = await usersApi.checkDuplicate({ email: submitData.email, role: 'worker', rut: submitData.rut });
      if (checkRes.data.exists) {
        setDuplicateWorker(checkRes.data.user);
        setPendingWorkerData(submitData);
        setAddingWorker(false);
        return;
      }
      await usersApi.create(submitData);
      toast.success('Trabajador agregado exitosamente');
      setWorkerForm(EMPTY_FORM);
      setShowAddWorker(false); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al agregar trabajador'); }
    finally { setAddingWorker(false); }
  };

  const handleUpdateDuplicateWorker = async () => {
    if (!duplicateWorker || !pendingWorkerData) return;
    try {
      await usersApi.update(duplicateWorker.id, pendingWorkerData);
      toast.success('Trabajador actualizado exitosamente');
      setDuplicateWorker(null); setPendingWorkerData(null);
      setWorkerForm(EMPTY_FORM);
      setShowAddWorker(false); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al actualizar'); }
  };

  const filteredWorkers = workers.filter(w =>
    `${w.first_name} ${w.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (w.rut && w.rut.includes(searchTerm))
  );

  const totalPages = Math.ceil(filteredWorkers.length / ITEMS_PER_PAGE);
  const paginatedWorkers = filteredWorkers.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  // Stats
  const maleCount = workers.filter(w => w.sexo && ['MASCULINO', 'M', 'HOMBRE', 'H'].includes(w.sexo.toUpperCase())).length;
  const femaleCount = workers.filter(w => w.sexo && ['FEMENINO', 'F', 'MUJER'].includes(w.sexo.toUpperCase())).length;
  const total = workers.length;
  const malePct = total > 0 ? ((maleCount / total) * 100).toFixed(1) : 0;
  const femalePct = total > 0 ? ((femaleCount / total) * 100).toFixed(1) : 0;
  const now = new Date();
  const ages = workers.map(w => {
    if (!w.fecha_nacimiento) return null;
    const birth = new Date(w.fecha_nacimiento);
    if (isNaN(birth.getTime())) return null;
    let age = now.getFullYear() - birth.getFullYear();
    const m = now.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
    return age;
  }).filter(a => a !== null);
  const avgAge = ages.length > 0 ? (ages.reduce((s, a) => s + a, 0) / ages.length).toFixed(1) : '-';

  if (loading) {
    return (<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" /></div>);
  }

  const setField = (k, v) => { setWorkerForm(f => ({ ...f, [k]: v })); if (fieldErrors[k]) setFieldErrors(fe => ({ ...fe, [k]: undefined })); };
  const inputCls = (k) => `bg-[#f8fafc] text-[#2B4B8C] ${fieldErrors[k] ? 'border-red-400 focus-visible:ring-red-400' : 'border-[#2B4B8C]/10'}`;
  const FieldError = ({ k }) => (fieldErrors[k] ? <p className="text-xs text-red-500" data-testid={`cw-error-${k}`}>{fieldErrors[k]}</p> : null);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contractor-workers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2B4B8C]">Trabajadores</h1>
          <p className="text-[#2B4B8C]/60">Trabajadores de tu empresa ({total})</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsBulkUploadOpen(true)} className="border-[#2B4B8C]/20 text-[#2B4B8C] hover:bg-[#2B4B8C]/5" data-testid="contractor-bulk-upload-btn">
            <UploadSimple className="mr-2" size={20} /> Carga Masiva
          </Button>
          <Button onClick={openCreate} className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="contractor-add-worker-btn">
            <Plus className="mr-2" size={20} /> Nuevo Trabajador
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {total > 0 && (
        <div className="grid grid-cols-3 gap-4" data-testid="contractor-stats">
          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <GenderMale size={22} className="text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-[#2B4B8C]/60">Hombres</p>
                <p className="text-xl font-bold text-[#2B4B8C]">{maleCount} <span className="text-sm font-normal text-[#2B4B8C]/50">({malePct}%)</span></p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-pink-500/10 flex items-center justify-center">
                <GenderFemale size={22} className="text-pink-500" />
              </div>
              <div>
                <p className="text-sm text-[#2B4B8C]/60">Mujeres</p>
                <p className="text-xl font-bold text-[#2B4B8C]">{femaleCount} <span className="text-sm font-normal text-[#2B4B8C]/50">({femalePct}%)</span></p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <CalendarBlank size={22} className="text-amber-500" />
              </div>
              <div>
                <p className="text-sm text-[#2B4B8C]/60">Edad Promedio del Personal</p>
                <p className="text-xl font-bold text-[#2B4B8C]">{avgAge} <span className="text-sm font-normal text-[#2B4B8C]/50">años</span></p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-4">
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/60" size={20} />
            <Input placeholder="Buscar por nombre, email o RUT..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" />
          </div>
        </CardContent>
      </Card>

      {/* Workers Table */}
      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#2B4B8C]/10 hover:bg-transparent">
                <TableHead className="text-[#2B4B8C]/60">RUT</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Nombre</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Cargo</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Email</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Teléfono</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Fecha Nac.</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Estado</TableHead>
                <TableHead className="text-[#2B4B8C]/60 text-center">QR</TableHead>
                <TableHead className="text-[#2B4B8C]/60 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedWorkers.map((w) => (
                <TableRow key={w.id} className="border-[#2B4B8C]/5 hover:bg-[#f8fafc]">
                  <TableCell className="text-[#2B4B8C]/60">{w.rut || '-'}</TableCell>
                  <TableCell className="text-[#2B4B8C] font-medium">{w.first_name} {w.last_name}</TableCell>
                  <TableCell className="text-[#2B4B8C]/60">{w.cargo || '-'}</TableCell>
                  <TableCell className="text-[#2B4B8C]/60">{w.email}</TableCell>
                  <TableCell className="text-[#2B4B8C]/60">{w.phone || '-'}</TableCell>
                  <TableCell className="text-[#2B4B8C]/60">{w.fecha_nacimiento ? new Date(w.fecha_nacimiento).toLocaleDateString('es-CL') : '-'}</TableCell>
                  <TableCell>
                    <Badge className={w.is_active ? 'bg-green-500/10 text-green-400 border-green-500/20 border' : 'bg-red-500/10 text-red-400 border-red-500/20 border'}>
                      {w.is_active ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Button variant="ghost" size="sm" onClick={() => setQrUser(w)} className="text-[#2B4B8C]/50 hover:text-[#2BBAA4]" title="QR Expediente de capacitaciones" data-testid={`qr-worker-${w.id}`}>
                      <QrCode size={18} />
                    </Button>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(w)} className="text-[#2B4B8C]/70 hover:text-[#2B4B8C]" data-testid={`edit-worker-${w.id}`}>
                      <PencilSimple size={16} className="mr-1" /> Editar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredWorkers.length === 0 && (
            <div className="text-center py-12 text-[#2B4B8C]/50">No se encontraron trabajadores</div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-[#2B4B8C]/10">
              <span className="text-sm text-[#2B4B8C]/60">
                Mostrando {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filteredWorkers.length)} de {filteredWorkers.length}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="border-[#2B4B8C]/10 text-[#2B4B8C]">
                  <CaretLeft size={16} className="mr-1" /> Anterior
                </Button>
                <span className="text-sm text-[#2B4B8C] font-medium px-2">{currentPage} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} className="border-[#2B4B8C]/10 text-[#2B4B8C]">
                  Siguiente <CaretRight size={16} className="ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Worker Dialog */}
      <Dialog open={showAddWorker} onOpenChange={setShowAddWorker}>
        <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2B4B8C]">{editingWorker ? 'Editar Trabajador' : 'Nuevo Trabajador'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddWorker} noValidate className="space-y-4" data-testid="cw-worker-form">
            <div className="space-y-2">
              <Label className="text-[#2B4B8C]/80">RUT *</Label>
              <Input value={workerForm.rut} onChange={(e) => setField('rut', e.target.value)} placeholder="12.345.678-9" className={inputCls('rut')} data-testid="cw-worker-rut" />
              <FieldError k="rut" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Nombre *</Label>
                <Input value={workerForm.first_name} onChange={(e) => setField('first_name', e.target.value)} className={inputCls('first_name')} data-testid="cw-worker-first-name" />
                <FieldError k="first_name" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Apellido *</Label>
                <Input value={workerForm.last_name} onChange={(e) => setField('last_name', e.target.value)} className={inputCls('last_name')} data-testid="cw-worker-last-name" />
                <FieldError k="last_name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Cargo *</Label>
                <Input value={workerForm.cargo} onChange={(e) => setField('cargo', e.target.value)} className={inputCls('cargo')} data-testid="cw-worker-cargo" />
                <FieldError k="cargo" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Sexo *</Label>
                <Select value={workerForm.sexo} onValueChange={(v) => setField('sexo', v)}>
                  <SelectTrigger className={inputCls('sexo')} data-testid="cw-worker-sexo"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent className="bg-white border-[#2B4B8C]/10">
                    <SelectItem value="MASCULINO">Masculino</SelectItem>
                    <SelectItem value="FEMENINO">Femenino</SelectItem>
                  </SelectContent>
                </Select>
                <FieldError k="sexo" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Email *</Label>
                <Input type="email" value={workerForm.email} onChange={(e) => setField('email', e.target.value)} className={inputCls('email')} data-testid="cw-worker-email" />
                <FieldError k="email" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Teléfono *</Label>
                <Input value={workerForm.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="+56912345678" className={inputCls('phone')} data-testid="cw-worker-phone" />
                <FieldError k="phone" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#2B4B8C]/80">Fecha de Nacimiento *</Label>
              <Input type="date" value={workerForm.fecha_nacimiento} onChange={(e) => setField('fecha_nacimiento', e.target.value)} className={inputCls('fecha_nacimiento')} data-testid="cw-worker-birthdate" />
              <FieldError k="fecha_nacimiento" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowAddWorker(false)} className="flex-1 border-[#2B4B8C]/10" data-testid="cw-worker-cancel">Cancelar</Button>
              <Button type="submit" disabled={addingWorker} className="flex-1 bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="cw-worker-submit">
                {addingWorker ? 'Guardando...' : editingWorker ? 'Guardar cambios' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <WorkerQrDialog user={qrUser} onClose={() => setQrUser(null)} />

      {/* Bulk Upload */}
      <BulkUploadDialog
        open={isBulkUploadOpen}
        onOpenChange={setIsBulkUploadOpen}
        title="Carga Masiva de Trabajadores"
        rutRequired={true}
        templateHeaders={['Rut', 'Nombre', 'Apellidos', 'Cargo', 'Sexo', 'Mail', 'Teléfono', 'Fecha Nacimiento']}
        fieldNames={['rut', 'first_name', 'last_name', 'cargo', 'sexo', 'email', 'phone', 'fecha_nacimiento']}
        templateExample={['12.345.678-9', 'Juan', 'Pérez', 'Operador', 'M', 'trabajador@email.com', '+56912345678', '15-01-1990']}
        onUpload={async (data) => {
          const sexoMap = { 'h': 'MASCULINO', 'hombre': 'MASCULINO', 'masculino': 'MASCULINO', 'm': 'FEMENINO', 'mujer': 'FEMENINO', 'f': 'FEMENINO', 'femenino': 'FEMENINO' };
          const sexoErrors = [];
          data.forEach((row, idx) => {
            if (row.sexo && row.sexo.trim()) {
              if (!sexoMap[row.sexo.trim().toLowerCase()]) {
                sexoErrors.push(`Fila ${idx + 1}: Sexo inválido "${row.sexo}"`);
              }
            }
          });
          if (sexoErrors.length > 0) {
            toast.error(sexoErrors.slice(0, 3).join('\n') + (sexoErrors.length > 3 ? `\n...y ${sexoErrors.length - 3} más` : ''));
            throw new Error('Validación fallida');
          }
          const users = data.map(row => ({
            rut: row.rut || null, first_name: row.first_name, last_name: row.last_name,
            cargo: row.cargo || null, sexo: row.sexo ? sexoMap[row.sexo.trim().toLowerCase()] || null : null,
            email: row.email, phone: row.phone || null, fecha_nacimiento: row.fecha_nacimiento || null,
            password: row.rut ? row.rut.replace(/[^0-9kK]/g, '') : `${row.email.split('@')[0]}123`,
            role: 'worker'
          }));
          const checkRes = await usersApi.checkBulkDuplicates({ users, role: 'worker' });
          if (checkRes.data.duplicate_count > 0) {
            setPendingBulkData(users); setBulkDuplicates(checkRes.data); setIsBulkUploadOpen(false);
            return null;
          }
          const res = await usersApi.bulkUpload({ users }); fetchData(); return res.data;
        }}
        entityName="trabajadores"
      />

      {/* Duplicate Dialogs */}
      <SingleDuplicateDialog
        open={!!duplicateWorker}
        onOpenChange={(open) => { if (!open) { setDuplicateWorker(null); setPendingWorkerData(null); } }}
        existingUser={duplicateWorker}
        onUpdate={handleUpdateDuplicateWorker}
        onCancel={() => { setDuplicateWorker(null); setPendingWorkerData(null); }}
      />
      <BulkDuplicateDialog
        open={!!bulkDuplicates}
        onOpenChange={(open) => { if (!open) { setBulkDuplicates(null); setPendingBulkData(null); } }}
        duplicates={bulkDuplicates?.duplicates || []}
        total={bulkDuplicates?.total || 0}
        onUpdateExisting={async () => {
          try {
            const res = await usersApi.bulkUploadUpdate({ users: pendingBulkData });
            toast.success(`${res.data.created} creados, ${res.data.updated} actualizados`);
            setBulkDuplicates(null); setPendingBulkData(null); fetchData();
          } catch { toast.error('Error al procesar'); }
        }}
        onSkipExisting={async () => {
          try {
            const res = await usersApi.bulkUpload({ users: pendingBulkData });
            toast.success(`${res.data.created} creados, ${res.data.skipped} omitidos`);
            setBulkDuplicates(null); setPendingBulkData(null); fetchData();
          } catch { toast.error('Error al procesar'); }
        }}
        onCancel={() => { setBulkDuplicates(null); setPendingBulkData(null); }}
      />
    </div>
  );
}
